import { gql } from "@apollo/client";
import React from "react";
import styled from "styled-components";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { MobileDatePicker } from "@mui/x-date-pickers";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  TextField,
  Typography,
  useMediaQuery,
  Button,
} from "@mui/material";
import { GraphqlMutation } from "graphqlUtils/GraphqlMutation";
import { media } from "media";
import dayjs from "dayjs_with_plugins";

const DELETE_MENU = gql`
  mutation DeleteMenu($id: ID!) {
    deleteMenu(id: $id) {
      id
      name
    }
  }
`;

const UPDATE_MENU = gql`
  mutation UpdateMenu($input: UpdateMenuInput!) {
    updateMenu(input: $input) {
      id
      name
      activeUntil
      activeFrom
    }
  }
`;

const StartEndDate = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${media.sm`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    width: 75%;
  `}
`;

const P = styled.p`
  font-size: 16px;
  position: relative;
  top: 16px;
  ${media.sm`
    top: 0;
    margin-bottom: 0;
  `}
`;

const useStyles = makeStyles({
  inputStyles: { marginBottom: "15px", width: "66%", maxWidth: "400px" },
  dialog: { padding: "30px 0px 30px 0px" },
  actions: { "& > button": { marginRight: "10px" } },
});

export const EditMenuModal = (props) => {
  const { open, onSubmit, onCancel, menu, refetchQueries } = props;
  const { inputStyles, dialog, actions } = useStyles();
  const theme = useTheme();
  const defaultState = {
    activeFrom: dayjs(menu.activeFrom, "YYYY-MM-DD", true),
    activeUntil: dayjs(menu.activeUntil, "YYYY-MM-DD", true),
  };
  const [dates, setDates] = React.useState(defaultState);

  const [currentName, updateName] = React.useState(menu.name);
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  React.useEffect(() => updateName(menu.name), [menu.name, updateName]);
  React.useEffect(
    () =>
      setDates({
        activeFrom: dayjs(menu.activeFrom),
        activeUntil: dayjs(menu.activeUntil),
      }),
    [menu, setDates],
  );

  return (
    <Dialog
      disableEscapeKeyDown
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      open={open}
      PaperProps={{ className: dialog }}
    >
      <DialogTitle>
        <Typography variant="h4" component="h4">
          Edit Menu {currentName}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          required
          className={inputStyles}
          label="name"
          value={currentName}
          onChange={(event) => updateName(event.target.value)}
        />
      </DialogContent>
      <DialogContent>
        <StartEndDate>
          <MobileDatePicker
            clearable
            variant="dialog"
            label="Start Date"
            format="MMMM Do YYYY"
            value={dates.activeFrom}
            onChange={(activeFrom) => {
              if (activeFrom) {
                setDates({ ...dates, activeFrom: activeFrom });
              } else {
                setDates(defaultState);
              }
            }}
          />
          <P>To</P>
          <MobileDatePicker
            clearable
            disabled={!dates.activeFrom}
            variant="dialog"
            minDate={dates.activeFrom}
            label="End date"
            format="MMMM Do YYYY"
            value={dates.activeUntil}
            onChange={(activeUntil) => {
              if (activeUntil) {
                setDates({ ...dates, activeUntil: activeUntil });
              } else {
                setDates({ ...dates, activeUntil });
              }
            }}
          />
        </StartEndDate>
      </DialogContent>
      <DialogActions className={actions}>
        <GraphqlMutation mutation={DELETE_MENU} withError>
          {(mutation) => {
            const onClick = async () => {
              const confirm = window.confirm(
                `This will permanently delete menu ${menu.name}. Are you sure?`,
              );
              if (confirm) {
                const result = await mutation({ variables: { id: menu.id }, refetchQueries });
                if (result.data) {
                  onSubmit();
                }
              }
            };
            return (
              <Button color="secondary" variant="outlined" onClick={onClick}>
                Delete Menu
              </Button>
            );
          }}
        </GraphqlMutation>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <GraphqlMutation mutation={UPDATE_MENU} withError>
          {(mutation) => {
            const onClick = async () => {
              const result = await mutation({
                variables: {
                  input: {
                    id: menu.id,
                    activeFrom: dates.activeFrom.format("YYYY-MM-DD"),
                    activeUntil: dates.activeUntil.format("YYYY-MM-DD"),
                    name: currentName,
                  },
                },
                refetchQueries,
              });

              if (result.data) {
                onSubmit();
              }
            };
            return (
              <Button color="primary" variant="outlined" onClick={onClick}>
                Save
              </Button>
            );
          }}
        </GraphqlMutation>
      </DialogActions>
    </Dialog>
  );
};
